import { text } from '#contract'
import type { InputDevice, LocalizedText } from '#contract'
import { useView } from '../app/use-view.ts'
import { KeyHint } from '../components/key-hint.tsx'
import { useT } from '../i18n/index.ts'
import styles from './inventory-screen.module.css'

interface Hint {
  /** A keycap, a gamepad button, or a word for touch ("Tap"). */
  readonly key: string | LocalizedText
  readonly does: LocalizedText
}

const TAKE = { all: text('inventory.hint.takeAll'), one: text('inventory.hint.takeOne') }
const PUT = { all: text('inventory.hint.putAll'), one: text('inventory.hint.putOne') }

/** The screen's controls for the current device, along the bottom. They follow what's held. */
export function InventoryHints() {
  const t = useT()
  const device = useView((view) => view.input.device)
  const holding = useView((view) => (view.inventory?.screen?.cursor ?? null) !== null)
  const close = useView((view) => view.input.labels.inventory)
  const hints = hintsFor(device, holding ? PUT : TAKE, close.gamepad)
  return (
    <ul class={styles.hints}>
      {hints.map(({ key, does }, i) => (
        <li key={i}>
          {typeof key !== 'string' ? (
            <span class={styles.gesture}>{t(key)}</span>
          ) : (
            <KeyHint label={key} variant={device === 'gamepad' ? 'button' : 'key'} />
          )}
          <span>{t(does)}</span>
        </li>
      ))}
    </ul>
  )
}

function hintsFor(
  device: InputDevice,
  verbs: { readonly all: LocalizedText; readonly one: LocalizedText },
  padClose: string,
): readonly Hint[] {
  const move = text('inventory.hint.move')
  const close = text('inventory.hint.close')
  if (device === 'gamepad') {
    return [
      { key: 'A', does: verbs.all },
      { key: 'X', does: verbs.one },
      { key: 'Y', does: move },
      { key: padClose === '' ? 'B' : `B / ${padClose}`, does: close },
    ]
  }
  if (device === 'touch') {
    return [
      { key: text('inventory.hint.tap'), does: verbs.all },
      { key: text('hud.hold'), does: verbs.one },
    ]
  }
  return [
    { key: 'LMB', does: verbs.all },
    { key: 'RMB', does: verbs.one },
    { key: 'Shift', does: move },
    { key: 'Esc', does: close },
  ]
}
